/**
 * roomOrder — the order EVERY list of a room's teams renders in.
 *
 * 🔴 WHY THIS FILE EXISTS. Ben, Sep 3 2026, on the deployed seat report: the
 * switcher chips above an open seat render in seat order, so the A+ sat ninth.
 * Three controls listed the same ten teams and each one sorted them its own way
 * — the room report by grade rank, the "your room so far" strip by projected
 * points, and the switcher not at all — which means the page ranked its teams
 * three different ways on one screen and the loudest of the three was data-entry
 * order. So there is one sort and it lives here, and the controls are consumers.
 *
 * 🔴 IT SORTS ON WHATEVER THE BADGE SAYS, WHICH IS THE POINT. A graded room is
 * ordered by the letter's own rank; an ungraded one by projected week 1 points,
 * which is the number those rows are already showing. A list ordered by
 * something it does not display is a list a reader cannot check.
 *
 * 🔴 IT RETURNS INDICES, NEVER REORDERED ROWS. The switcher is a control: its
 * `active` seat and its `onPick(i)` are the CALLER's indices, and a helper that
 * handed back shuffled rows would quietly make every seat click open the wrong
 * team. Indices keep the mapping honest and visible at the call site.
 *
 * The tie-break is the original index, so the order is total and a re-render
 * cannot reshuffle two teams that drew level.
 */

/**
 * Best first.
 *
 * @param {Array} rows [{ rank, week1 }] — `rank` is the room grade's own rank
 *   (roomGradesBySurplus), null or absent until the room has earned letters.
 * @returns {number[]} the caller's indices, best first. An empty or absent list
 *   returns an empty array; a list with nothing to rank on keeps its own order.
 */
export function roomRowOrder(rows) {
  const list = rows || [];
  const idx = list.map((_, i) => i);
  // 🔴 ALL OR NOTHING ON THE LETTERS, matching the rule that produces them:
  // roomGradesBySurplus is only ever called on a full, fully priced room, so a
  // half-graded list is not a state this can be in. Ranking a mixture would put
  // the ungraded seats somewhere arbitrary and call it a leaderboard.
  const graded = list.length > 0 && list.every((r) => Number.isFinite(r && r.rank));
  if (graded) return idx.sort((a, b) => list[a].rank - list[b].rank || a - b);
  return idx.sort((a, b) => (list[b].week1 || 0) - (list[a].week1 || 0) || a - b);
}
